import React from "react";

/**
 * Shared error / empty state blocks for feature panels.
 *
 * Consolidates the per-feature copies and gives GA4/Ads panels that lack one a
 * real error branch. Live audit (2026-07-01) found two distinct error looks in
 * the codebase, split by feature — this component carries both so migrations
 * stay behavior-preserving:
 *
 *  ErrorState `variant`:
 *   - "strip" (default): red-tinted alert box. The most-duplicated block —
 *      byte-identical ×9 in EI, ×1 in Ads (BudgetPacing), ×1 in teammate
 *      BookingsPage. `bg-red-50 … border-red-200 … rounded-lg p-3 text-red-700`.
 *   - "inset": bare red text on transparent bg, for dropping inside a panel
 *      body that already has its own header. Matches CI's ~8 copies + `PanelError`.
 *      Padding defaults to `p-5`; override via the `padding` prop (a few CI
 *      sites use `px-5 py-6` / `p-6`).
 *   - "card": own white rounded-card wrapper + optional `title`. Matches CI
 *      `ErrorBox` / `MetricCard` and the teammate CompanyManagement error card.
 *
 *  EmptyState `variant` (empties never render as a red strip):
 *   - "inset" (default): centered muted message inside an existing panel shell.
 *      Matches CI `PanelEmpty` and EI's per-panel empties.
 *   - "card": own card wrapper + optional `title`. Matches CI `EmptyBox`,
 *      GA4 `EmptyState`, and the CI page-level "No competitive data yet" card.
 *
 * Font floor honored: body copy is `text-sm` (14px) everywhere.
 */

const CARD_SHELL =
  "bg-white dark:bg-gray-900 rounded-xl shadow border overflow-hidden";

const STRIP =
  "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400";

// Same red palette as STRIP but larger radius/padding — the page-level KPI-fetch
// banner used at the top of GoogleAdsPage / GoogleAnalyticsPage (a page banner, not
// a per-panel strip).
const BANNER =
  "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400";

const CardHeader = ({ title }) =>
  title ? (
    <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
        {title}
      </h3>
    </div>
  ) : null;

const RetryButton = ({ onRetry }) =>
  onRetry ? (
    <button
      type="button"
      onClick={onRetry}
      className="mt-3 inline-flex items-center gap-1 rounded-lg border border-red-200 dark:border-red-900/50 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
    >
      Try again
    </button>
  ) : null;

/**
 * @param {object}   props
 * @param {string}  [props.message]  Body copy. Defaults to a generic load-failure line.
 * @param {string}  [props.title]    Card-variant header text (ignored when variant="strip"/"inset"/"banner").
 * @param {"strip"|"banner"|"inset"|"card"} [props.variant]
 * @param {string}  [props.padding]  Inset-variant padding classes. Default "p-5".
 * @param {() => void} [props.onRetry]  If provided, renders a "Try again" button.
 * @param {string}  [props.className]
 */
export const ErrorState = ({
  message = "Couldn't load this data.",
  title,
  variant = "strip",
  padding = "p-5",
  onRetry,
  className = "",
}) => {
  if (variant === "card") {
    return (
      <div
        className={`${CARD_SHELL} border-red-200 dark:border-red-900/50 ${className}`}
        role="alert"
      >
        <CardHeader title={title} />
        <div className="p-5 text-sm text-red-600 dark:text-red-400">
          <p>{message}</p>
          <RetryButton onRetry={onRetry} />
        </div>
      </div>
    );
  }

  if (variant === "inset") {
    return (
      <div className={`${padding} text-sm text-red-600 dark:text-red-400 ${className}`} role="alert">
        <p>{message}</p>
        <RetryButton onRetry={onRetry} />
      </div>
    );
  }

  if (variant === "banner") {
    return (
      <div className={`${BANNER} ${className}`} role="alert">
        <p>{message}</p>
        <RetryButton onRetry={onRetry} />
      </div>
    );
  }

  // "strip" — the default, most-duplicated look.
  return (
    <div className={`${STRIP} ${className}`} role="alert">
      <p>{message}</p>
      <RetryButton onRetry={onRetry} />
    </div>
  );
};

/**
 * @param {object}   props
 * @param {string}  [props.message]  Body copy. Defaults to GA4's existing "No data for this period".
 * @param {string}  [props.title]    Card-variant header text (ignored when variant="inset").
 * @param {"inset"|"card"} [props.variant]
 * @param {string}  [props.padding]  Body padding classes. Default "p-8". GA4's per-table
 *      empties use "py-12" (full-width centered, no side padding) — pass that to preserve
 *      their look when migrating.
 * @param {React.ReactNode} [props.icon]  Optional glyph shown above the message (card variant).
 * @param {string}  [props.className]
 */
export const EmptyState = ({
  message = "No data for this period",
  title,
  variant = "inset",
  padding = "p-8",
  icon,
  className = "",
}) => {
  const body = (
    <div className={`${padding} text-center text-sm text-gray-500 dark:text-gray-400`}>
      {icon && <div className="flex justify-center mb-3">{icon}</div>}
      <p>{message}</p>
    </div>
  );

  if (variant === "card") {
    return (
      <div
        className={`${CARD_SHELL} border-gray-200 dark:border-gray-800 ${className}`}
      >
        <CardHeader title={title} />
        {body}
      </div>
    );
  }

  return <div className={className}>{body}</div>;
};
